import React, { useState, Component } from 'react';
import { CheckCircle2, ClipboardList } from 'lucide-react';
export interface TaskMeta {
  assignee?: string;
  dueDate?: string;
  priority?: 'Low' | 'Medium' | 'High' | 'Urgent';
  status?: 'Open' | 'In Progress' | 'Complete';
  linkedEntity?: string;
  isOverdue?: boolean;
}
export interface TimelineEventProps {
  /** Event title */
  title: string;
  /** Event description */
  description: string;
  /** Event date/time string */
  date: string;
  /** Optional author name */
  author?: string;
  /** Icon component to display (any Lucide icon) */
  icon: ComponentType<{
    className?: string;
    style?: React.CSSProperties;
  }>;
  /** Hex color for the icon */
  iconColor: string;
  /** Whether this is the last event (no connector line) */
  isLast?: boolean;
  /** Event type identifier */
  eventType?: string;
  /** Task-specific metadata */
  taskMeta?: TaskMeta;
  /** Optional data-id for component identification */
  'data-id'?: string;
}
const priorityColors: Record<string, {
  bg: string;
  text: string;
  border: string;
}> = {
  Low: {
    bg: 'bg-[#D1FAE5]',
    text: 'text-[#065F46]',
    border: 'border-[#A7F3D0]'
  },
  Medium: {
    bg: 'bg-[#FEF3C7]',
    text: 'text-[#92400E]',
    border: 'border-[#FDE68A]'
  },
  High: {
    bg: 'bg-[#FFEDD5]',
    text: 'text-[#9A3412]',
    border: 'border-[#FED7AA]'
  },
  Urgent: {
    bg: 'bg-[#FEE2E2]',
    text: 'text-[#991B1B]',
    border: 'border-[#FECACA]'
  }
};
const statusColors: Record<string, {
  bg: string;
  text: string;
}> = {
  Open: {
    bg: 'bg-[#DBEAFE]',
    text: 'text-[#1E40AF]'
  },
  'In Progress': {
    bg: 'bg-[#EDE9FE]',
    text: 'text-[#5B21B6]'
  },
  Complete: {
    bg: 'bg-[#D1FAE5]',
    text: 'text-[#065F46]'
  }
};
export function TimelineEvent({
  title,
  description,
  date,
  author,
  icon: Icon,
  iconColor,
  isLast = false,
  eventType,
  taskMeta,
  'data-id': dataId
}: TimelineEventProps) {
  const [expanded, setExpanded] = useState(false);
  const isTask = eventType === 'task' && taskMeta;
  const isCompleted = isTask && taskMeta?.status === 'Complete';
  const isOverdue = isTask && taskMeta?.isOverdue;
  // For completed tasks, use a green checkmark icon
  const TaskIcon = isCompleted ? CheckCircle2 : ClipboardList;
  const taskIconColor = isCompleted ? '#10B981' : '#8B5CF6';
  if (isTask && taskMeta) {
    const priority = taskMeta.priority || 'Medium';
    const pColors = priorityColors[priority] || priorityColors.Medium;
    const status = taskMeta.status || 'Open';
    const sColors = statusColors[status] || statusColors.Open;
    return <li className="relative" data-id="element-3220">
        {!isLast && <div className="absolute left-4 top-10 bottom-0 w-px bg-[#E2E8F0]" aria-hidden="true" data-id="element-3221" />}
        <div className={`flex gap-4 cursor-pointer group rounded-lg -mx-2 px-2 py-1 transition-colors hover:bg-[#F8FAFC] ${isCompleted ? 'opacity-70' : ''}`} onClick={() => setExpanded(!expanded)} role="button" tabIndex={0} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded);
      }} aria-expanded={expanded} aria-label={`Task: ${title}`} data-id="element-3222">
          {/* Icon */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{
          backgroundColor: `${taskIconColor}15`
        }} aria-hidden="true" data-id="element-3223">
            <TaskIcon className="w-4 h-4" style={{
            color: taskIconColor
          }} data-id="element-3224" />
          </div>

          {/* Content */}
          <div className="flex-1 pb-6" data-id="element-3225">
            <div className="flex items-start justify-between gap-4" data-id="element-3226">
              <div className="flex-1" data-id="element-3227">
                {/* Title */}
                <div className={`font-medium text-[14px] ${isCompleted ? 'line-through text-[#94A3B8]' : 'text-[#0F172A]'}`} data-id="element-3228">
                  {title}
                </div>

                {/* Task metadata row */}
                <div className="flex flex-wrap items-center gap-2 mt-1.5" data-id="element-3229">
                  {/* Priority badge */}
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold border ${pColors.bg} ${pColors.text} ${pColors.border}`} data-id="element-3230">
                    {priority}
                  </span>

                  {/* Status badge */}
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${sColors.bg} ${sColors.text}`} data-id="element-3231">
                    {status}
                  </span>

                  {/* Assignee */}
                  {taskMeta.assignee && <span className="text-[12px] text-[#64748B]" data-id="element-3232">
                      → {taskMeta.assignee}
                    </span>}

                  {/* Due date */}
                  {taskMeta.dueDate && <span className={`text-[12px] font-medium ${isOverdue ? 'text-[#EF4444]' : isCompleted ? 'text-[#94A3B8]' : 'text-[#64748B]'}`} data-id="element-3233">
                      {isCompleted ? `Completed ${taskMeta.dueDate}` : isOverdue ? 'Overdue' : `Due: ${taskMeta.dueDate}`}
                    </span>}
                </div>
              </div>
              <time className="text-[12px] text-[#94A3B8] whitespace-nowrap" data-id="element-3234">
                {date}
              </time>
            </div>

            {/* Expanded detail panel */}
            {expanded && <div className="mt-3 p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md text-[13px] space-y-2" data-id="element-3235">
                <div className="flex justify-between" data-id="element-3236">
                  <span className="text-[#64748B]" data-id="element-3237">Assignee</span>
                  <span className="font-medium text-[#0F172A]" data-id="element-3238">
                    {taskMeta.assignee || '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-3239">
                  <span className="text-[#64748B]" data-id="element-3240">Priority</span>
                  <span className={`font-medium ${pColors.text}`} data-id="element-3241">
                    {priority}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-3242">
                  <span className="text-[#64748B]" data-id="element-3243">Due Date</span>
                  <span className={`font-medium ${isOverdue ? 'text-[#EF4444]' : 'text-[#0F172A]'}`} data-id="element-3244">
                    {taskMeta.dueDate || '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-3245">
                  <span className="text-[#64748B]" data-id="element-3246">Status</span>
                  <span className={`font-medium ${sColors.text}`} data-id="element-3247">
                    {status}
                  </span>
                </div>
                {taskMeta.linkedEntity && <div className="flex justify-between" data-id="element-3248">
                    <span className="text-[#64748B]" data-id="element-3249">Linked To</span>
                    <span className="font-medium text-[#0EA5E9]" data-id="element-3250">
                      {taskMeta.linkedEntity}
                    </span>
                  </div>}
                {!isCompleted && <div className="pt-2 border-t border-[#E2E8F0] flex gap-2" data-id="element-3251">
                    <button className="h-7 px-3 bg-[#10B981] text-white rounded text-[12px] font-medium hover:bg-[#059669] transition-colors" data-id="element-3252">
                      Mark Complete
                    </button>
                    <button className="h-7 px-3 bg-white border border-[#CBD5E1] text-[#475569] rounded text-[12px] font-medium hover:bg-[#F1F5F9] transition-colors" data-id="element-3253">
                      Edit Task
                    </button>
                  </div>}
              </div>}
          </div>
        </div>
      </li>;
  }
  // Default non-task rendering
  return <li className="relative" data-id="element-3254">
      {/* Timeline connector line */}
      {!isLast && <div className="absolute left-4 top-10 bottom-0 w-px bg-[#E2E8F0]" aria-hidden="true" data-id="element-3255" />}
      <div className="flex gap-4" data-id="element-3256">
        {/* Icon */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{
        backgroundColor: `${iconColor}15`
      }} aria-hidden="true" data-id="element-3257">
          <Icon className="w-4 h-4" style={{
          color: iconColor
        }} data-id="element-3258" />
        </div>
        {/* Content */}
        <div className="flex-1 pb-6" data-id="element-3259">
          <div className="flex items-start justify-between gap-4" data-id="element-3260">
            <div className="flex-1" data-id="element-3261">
              <div className="font-medium text-[14px] text-[#0F172A]" data-id="element-3262">
                {title}
              </div>
              <div className="text-[14px] text-[#475569] mt-1" data-id="element-3263">
                {description}
              </div>
              {author && <div className="text-[12px] text-[#94A3B8] mt-1" data-id="element-3264">
                  by {author}
                </div>}
            </div>
            <time className="text-[12px] text-[#94A3B8] whitespace-nowrap" data-id="element-3265">
              {date}
            </time>
          </div>
        </div>
      </div>
    </li>;
}